import React, { useCallback, useEffect, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import {
  FetchStatus,
  LanderCookie,
  System,
  EventType,
  ErrorCode,
  QueryParam,
} from "../commons/Constants";
import {
  yahooXmlFetch,
  getYahooRSUri,
  extractPartnerTermsQs,
  extractRelatedTermItems,
} from "./yahooFeed";
import { sendYahooEvent } from "./yahooEvent";
import { useLanderConfig } from "../commons/ConfigContext";
import { trackImpression, trackClick } from "../commons/signals";
import { getCookie } from "../commons/HelperFunctions";
import { YahooAds } from "./YahooAds";
import { YahooRS } from "./YahooRS";
import { BlendConfig, RSItem, TermOrigin } from "./types";
import { blendTerms } from "./blendTerms";
import { SearchBox } from "./SearchBox";
import { useYahooSingleLandersScript } from "./useYahooSingleLandersScript";
import { useRollerAdsBackfill } from "./useRollerAdsBackfill";
import { useOptimizedKeywordsExperiment } from "../experimentation/useOptimizedKeywordsExperiment";
import { useBlendedKeywordsExperiment } from "../experimentation/useBlendedKeywordsExperiment";
import { RollerAdsKeywordLinks } from "../rollerads/RollerAdsKeywordLinks";
import { fetchRollerAds, getRollerAdsPid } from "../rollerads/rollerAdsFeed";
import { sendRollerAdsEvent } from "../rollerads/rollerAdsEvent";
import { isValidRedirectUrl } from "../rollerads/rollerAdsUtils";
import Logger from "../commons/Logger";
import { llmKeywordTerms } from "../commons/llmKeywords";
import classes from "./YahooAds.module.scss";
import PropTypes from "prop-types";
import classNames from "classnames";

interface YahooAdContentProps {
  isAdClicked: boolean;
  xmlResult: Document | null;
  keywords: string[];
  maxAds?: number;
  maxRS?: number;
  adIcon?: React.ReactNode;
  headerMessageId?: string;
  /** Non-null only for the blend arm; null means render as before. */
  blendConfig?: BlendConfig | null;
  /** PARKING-5307: the partner terms came from the API's LLM keywords. */
  partnerTermsFromLlm?: boolean;
}

const YahooAdContent: React.FC<YahooAdContentProps> = ({
  isAdClicked,
  xmlResult,
  keywords,
  maxAds,
  maxRS,
  adIcon,
  headerMessageId,
  blendConfig,
  partnerTermsFromLlm,
}) => {
  const partnerTermsQs = extractPartnerTermsQs(xmlResult);

  let relatedContent: React.ReactNode;
  if (isAdClicked) {
    relatedContent = (
      <YahooAds xmlResult={ xmlResult } maxAds={ maxAds } adIcon={ adIcon } />
    );
  } else {
    let rsItems: RSItem[];
    if (blendConfig) {
      rsItems = blendTerms({
        partnerTerms: keywords,
        partnerTermsQs,
        yahooItems: extractRelatedTermItems(xmlResult),
        config: {
          ...blendConfig,
          // Compose for the rows the layout will actually render. YahooRS slices
          // to maxRS afterwards, so blending for more would drop whichever rows
          // landed last and skew the ratio the cohort asked for.
          totalRows: Math.min(
            blendConfig.totalRows,
            maxRS ?? blendConfig.totalRows
          ),
        },
      });
    } else if (partnerTermsQs && keywords.length > 0) {
      rsItems = keywords.map((keyword) => ({
        term: keyword,
        qs: partnerTermsQs,
        searchQuery: keyword,
        origin: TermOrigin.PARTNER,
      }));
    } else {
      rsItems = extractRelatedTermItems(xmlResult);
    }
    relatedContent = (
      <YahooRS
        items={ rsItems }
        maxRS={ maxRS }
        headerMessageId={ headerMessageId }
        partnerTermsFromLlm={ partnerTermsFromLlm }
      />
    );
  }

  return (
    <>
      {relatedContent}
      <SearchBox xmlResult={ xmlResult } />
    </>
  );
};

YahooAdContent.propTypes = {
  isAdClicked: PropTypes.bool,
  xmlResult: PropTypes.instanceOf(Document),
};

interface YahooContentProps {
  maxAds?: number;
  maxRS?: number;
  adIcon?: React.ReactNode;
  headerMessageId?: string;
  /** Send RollerAds backfill keyword clicks to the second 1-click account (pid 387). */
  keywordClickAccount387?: boolean;
}

function getAdCount(xmlResult: Document | null): number {
  return (
    xmlResult?.getElementById("adListings")?.getElementsByTagName("Listing")
      ?.length ?? 0
  );
}

async function handleKeywordClick(
  keyword: string,
  landerConfig: any,
  setBackfillFetchState: (s: number) => void,
  keywordClickAccount387: boolean
) {
  setBackfillFetchState(FetchStatus.PENDING);
  const pid = getRollerAdsPid({ keywordClick: true, keywordClickAccount387 });

  sendRollerAdsEvent(EventType.KEYWORD_CLICK, landerConfig, {
    pid,
    keyword,
    isBackfill: true,
  });

  try {
    const result = await fetchRollerAds({
      rootDomain: landerConfig.domain.rootDomain,
      customerId: landerConfig.customerId,
      keywords: [keyword],
      keywordClick: true,
      ...(keywordClickAccount387 && { keywordClickAccount387 }),
    });

    if (result.status === 200 && result.ads.length > 0) {
      const firstAd = result.ads[0];

      if (!isValidRedirectUrl(firstAd.url)) {
        setBackfillFetchState(FetchStatus.FAILURE);
        sendRollerAdsEvent(EventType.ERROR, landerConfig, {
    pid,
          errorCode: ErrorCode.ERROR_ROLLERADS_INVALID_URL,
          isBackfill: true,
        });
        Logger.error("RollerAds backfill invalid redirect URL:", firstAd.url);
        return;
      }

      trackClick({ section: "ads", widget: "ad_redirect" });
      sendRollerAdsEvent(EventType.AD_REDIRECT, landerConfig, {
    pid,
        bid: firstAd.bid,
        campaignId: firstAd.campaign_id,
        adUrl: firstAd.url,
        keyword,
        isBackfill: true,
      });

      window.location.href = firstAd.url;
    } else {
      setBackfillFetchState(FetchStatus.FAILURE);
      sendRollerAdsEvent(EventType.ERROR, landerConfig, {
    pid,
        errorCode:
          result.status === 0
            ? ErrorCode.ERROR_ROLLERADS_BLOCKED
            : ErrorCode.ERROR_ROLLERADS_NO_ADS,
        isBackfill: true,
      });
    }
  } catch (error) {
    setBackfillFetchState(FetchStatus.FAILURE);
    sendRollerAdsEvent(EventType.ERROR, landerConfig, {
    pid,
      errorCode: ErrorCode.ERROR_ROLLERADS_BLOCKED,
      isBackfill: true,
    });
    Logger.error("RollerAds backfill fetch failed:", error);
  }
}

interface YahooBackfillProps {
  keywords: string[];
  landerConfig: any;
  keywordClickAccount387: boolean;
}

const YahooBackfill: React.FC<YahooBackfillProps> = ({
  keywords,
  landerConfig,
  keywordClickAccount387,
}) => {
  const [backfillFetchState, setBackfillFetchState] = useState<number>(
    FetchStatus.PENDING
  );
  const isClickInFlight = useRef(false);
  const hasBackfillEventFired = useRef(false);

  useEffect(() => {
    if (hasBackfillEventFired.current) return;
    hasBackfillEventFired.current = true;
    sendRollerAdsEvent(EventType.ROLLERADS_BACKFILL_TRIGGER, landerConfig, {
      isBackfill: true,
    });
  }, [landerConfig]);

  const handleBackfillKeywordClick = useCallback(
    (keyword: string) => {
      if (isClickInFlight.current) return;
      isClickInFlight.current = true;
      handleKeywordClick(
        keyword,
        landerConfig,
        setBackfillFetchState,
        keywordClickAccount387
      ).finally(() => {
        isClickInFlight.current = false;
      });
    },
    [landerConfig, keywordClickAccount387]
  );

  return (
    <RollerAdsKeywordLinks
      keywords={ keywords }
      fetchState={ backfillFetchState }
      onKeywordClick={ handleBackfillKeywordClick }
    />
  );
};

function useYahooFetch(
  landerConfig: any,
  isAdClicked: boolean,
  typeTagOverride?: string
) {
  const [fetchState, setFetchState] = useState<number>(FetchStatus.PENDING);
  const [xmlResult, setXmlResult] = useState<Document | null>(null);
  // An experiment hook that owns the tag resolves it during render and passes it here.
  // Waiting for its dispatch to reach `landerConfig` instead would fetch once untagged
  // and again once the tag lands, doubling the arm's RS requests (PARKING-5269).
  const typeTag = typeTagOverride ?? landerConfig.yahoo?.typeTag;

  useEffect(() => {
    if (!getCookie(LanderCookie.CLIENT_IP)) {
      setFetchState(FetchStatus.BLOCKED);
      setXmlResult(null);
      return;
    }
    let ignore = false;

    let yahooUrl: string;
    if (isAdClicked) yahooUrl = window.location.search;
    else
      yahooUrl = getYahooRSUri({
        rootDomain: landerConfig.domain.rootDomain,
        system: landerConfig.system,
        customerId: landerConfig.customerId,
        typeTag,
      });

    yahooXmlFetch(yahooUrl).then((v) => {
      if (ignore) return;
      setFetchState(v.fetchStatus);
      setXmlResult(v.xmlResult);

      if (v.fetchStatus === FetchStatus.FAILURE) {
        sendYahooEvent(EventType.ERROR, landerConfig, {
          yahooApiMessage: v.message,
          errorCode: isAdClicked
            ? ErrorCode.ERROR_YAHOO_FAILED_FETCH_ADS
            : ErrorCode.ERROR_YAHOO_FAILED_FETCH_RS,
        });
      }
    });

    return () => {
      ignore = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    landerConfig.domain.rootDomain,
    landerConfig.system,
    landerConfig.customerId,
    typeTag,
    isAdClicked,
  ]);

  return { fetchState, xmlResult };
}

interface YahooContentBodyProps {
  fetchState: number;
  shouldBackfill: boolean;
  backfillKeywords: string[];
  keywords: string[];
  landerConfig: any;
  isAdClicked: boolean;
  xmlResult: Document | null;
  maxAds?: number;
  maxRS?: number;
  adIcon?: React.ReactNode;
  headerMessageId?: string;
  blendConfig?: BlendConfig | null;
  partnerTermsFromLlm?: boolean;
  keywordClickAccount387: boolean;
}

const YahooContentBody: React.FC<YahooContentBodyProps> = ({
  fetchState,
  shouldBackfill,
  backfillKeywords,
  keywords,
  landerConfig,
  isAdClicked,
  xmlResult,
  maxAds,
  maxRS,
  adIcon,
  headerMessageId,
  blendConfig,
  partnerTermsFromLlm,
  keywordClickAccount387,
}) => {
  if (fetchState === FetchStatus.PENDING) {
    return null;
  }
  if (shouldBackfill) {
    return (
      <YahooBackfill
        keywords={ backfillKeywords }
        landerConfig={ landerConfig }
        keywordClickAccount387={ keywordClickAccount387 }
      />
    );
  }
  if (fetchState === FetchStatus.SUCCESS) {
    return (
      <YahooAdContent
        isAdClicked={ isAdClicked }
        xmlResult={ xmlResult }
        keywords={ keywords }
        maxAds={ maxAds }
        maxRS={ maxRS }
        adIcon={ adIcon }
        headerMessageId={ headerMessageId }
        blendConfig={ blendConfig }
        partnerTermsFromLlm={ partnerTermsFromLlm }
      />
    );
  }
  if (
    fetchState === FetchStatus.FAILURE ||
    fetchState === FetchStatus.BLOCKED
  ) {
    return (
      <div className={ classes.blockedContent }>
        <FormattedMessage id="contentBlockedText" />
      </div>
    );
  }
  return null;
};

// Resolve the keywords supplied to Yahoo Partner Supplied Terms (PARKING-5154).
// Precedence, first match wins:
//   1. 5269 blend treatment        -> optimizedKeywords (maybe none)
//   2. 5211 treatment              -> optimizedKeywords (maybe none)
//   3. ?term= override             -> merged keywords
//   4. owner-set DB/CSV keywords   -> merged keywords
//   5. LLM keywords (PARKING-5307) -> llmKeywords, which the caller takes from
//      llmKeywordTerms and which is empty unless the switch is on
//   6. otherwise none: Yahoo uses its own relatedTerms.
// Tokenizer keywords never become Partner Supplied Terms.
//
// An experiment arm's optimizedKeywords always win and are never backfilled
// with LLM terms, and owner-set keywords outrank the LLM terms.
//
// Exported for its own test: the two arms below must be shown to be independent,
// which cannot be asserted through a render that can only ever report one arm.
export function resolvePartnerTermSelection({
  mergedKeywords,
  inBlendTreatment,
  inOptimizedKeywordsTreatment,
  optimizedKeywords,
  keywordSource,
  hasTermOverride,
  llmKeywords,
}: {
  mergedKeywords?: string[];
  inBlendTreatment: boolean;
  inOptimizedKeywordsTreatment: boolean;
  optimizedKeywords?: string[];
  keywordSource?: string;
  hasTermOverride: boolean;
  llmKeywords?: string[];
}): { terms: string[]; fromLlm: boolean } {
  // PARKING-5269 — blended keywords. Deliberately its own branch rather than
  // reusing 5211's flag: the arms are separate experiments, and reading one from
  // the other would make retiring either of them a guessing game.
  if (inBlendTreatment) {
    return { terms: optimizedKeywords ?? [], fromLlm: false };
  }
  // PARKING-5211 — all our keywords, or none.
  if (inOptimizedKeywordsTreatment) {
    return { terms: optimizedKeywords ?? [], fromLlm: false };
  }
  if (mergedKeywords?.length) {
    if (hasTermOverride) return { terms: mergedKeywords, fromLlm: false };
    if (keywordSource === "DB" || keywordSource === "CSV") {
      return { terms: mergedKeywords, fromLlm: false };
    }
  }
  // PARKING-5307 — LLM keywords for traffic nobody set keywords for.
  if (llmKeywords?.length) return { terms: llmKeywords, fromLlm: true };
  return { terms: [], fromLlm: false };
}

export function resolvePartnerTerms(
  args: Parameters<typeof resolvePartnerTermSelection>[0]
): string[] {
  return resolvePartnerTermSelection(args).terms;
}

export const YahooContent: React.FC<YahooContentProps> = ({
  maxAds,
  maxRS,
  adIcon,
  headerMessageId,
  keywordClickAccount387 = false,
}) => {
  const { landerConfig, mergedConfig, queryConfig } = useLanderConfig();
  const { isBackfillEligible } = useRollerAdsBackfill();
  const { inOptimizedKeywordsTreatment } = useOptimizedKeywordsExperiment();
  const { inBlendTreatment, blendConfig, yahooTypeTag } =
    useBlendedKeywordsExperiment();

  const isAdClicked = new URLSearchParams(window.location.search).has("qs");
  const hasTermOverride = !!queryConfig[QueryParam.TERM];
  const { terms: partnerTerms, fromLlm: partnerTermsFromLlm } =
    resolvePartnerTermSelection({
      mergedKeywords: mergedConfig.keywords,
      inBlendTreatment,
      inOptimizedKeywordsTreatment,
      optimizedKeywords: landerConfig.optimizedKeywords,
      keywordSource: landerConfig.adSense?.keywordSource,
      hasTermOverride,
      llmKeywords: llmKeywordTerms(landerConfig),
    });

  const { fetchState, xmlResult } = useYahooFetch(
    landerConfig,
    isAdClicked,
    yahooTypeTag
  );

  useYahooSingleLandersScript(xmlResult);

  useEffect(() => {
    sendYahooEvent(EventType.VISIT, landerConfig);
    trackImpression({ section: "page", widget: "lander_page" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const backfillKeywords = mergedConfig.keywords?.length
    ? mergedConfig.keywords
    : landerConfig.adSense?.keywords ?? [];

  const shouldBackfill =
    isAdClicked &&
    isBackfillEligible &&
    backfillKeywords.length > 0 &&
    fetchState !== FetchStatus.PENDING &&
    fetchState !== FetchStatus.BLOCKED &&
    (fetchState === FetchStatus.FAILURE || getAdCount(xmlResult) === 0);

  return (
    <div
      className={ classNames(classes.yahooFeed, {
        [classes.pwYahoo]: landerConfig.system === System.PARKWEB,
      }) }
    >
      <YahooContentBody
        fetchState={ fetchState }
        shouldBackfill={ shouldBackfill }
        backfillKeywords={ backfillKeywords }
        keywords={ partnerTerms }
        landerConfig={ landerConfig }
        isAdClicked={ isAdClicked }
        xmlResult={ xmlResult }
        maxAds={ maxAds }
        maxRS={ maxRS }
        adIcon={ adIcon }
        headerMessageId={ headerMessageId }
        blendConfig={ inBlendTreatment ? blendConfig : null }
        partnerTermsFromLlm={ partnerTermsFromLlm }
        keywordClickAccount387={ keywordClickAccount387 }
      />
    </div>
  );
};
